<template>
  <div
    class="card"
    :class="{flipped: isFlipped, selected: isSelected, 'drag-over': dragOver, selectable: selectable }"
    :style="reelStyle"
    draggable="true"
    @dragstart="handleDragStart"
    @dragover="handleDragOver"
    @drop="handleDrop"
    @dragenter="handleDragEnter"
    @dragleave="handleDragLeave"
    @click="handleClick"
  >
    <div class="front">
      <!-- Front of the card (card back/department image) -->
      <div class="card-image" v-if="frontImage">
        <img :src="frontImage" :alt="`${department} card`" />
      </div>
      <div class="card-image placeholder" v-else>
        <div class="department-pattern"></div>
      </div>
    </div>
    <div class="back" :style="backStyle">
      <!-- Back of the card (content) -->
      <span v-if="gameStore.isDemoSession" class="card-demo-ribbon">Demo</span>
      <h2 :style="titleStyle">{{ title}} </h2>
      <div v-html="content" class="card-content"></div>
      
      <span v-if="gameStore.showTiers || isInJobsDone" class="rank" :class="`ranking-${ranking}`">T{{ ranking }}</span>
   
      <!-- When explanation is available, emit event so parent can show modal -->
       <transition name="fade">
      <button v-if="explanation && (gameStore.showExplanations || isInJobsDone)" @click.stop="emitShowExplanation" class="explanation">
        <i class="fa-regular fa-circle-question fa-3x"></i>
      </button>
      </transition>

    </div>
  </div>
</template>

<script setup>
import { useGameStore } from '@/stores/gameStore';
import { ref, computed } from 'vue';

const gameStore = useGameStore(); // Initialize the store

const props = defineProps({
  title: {
    type: String,
    required: true,
  },
  content: {
    type: String,
    required: true,
  },
  ranking: {
    type: [String, Number],
    required: true,
  },
  explanation: {
    type: String,
    default: '',
  },

  department: {
    type: String,
    default: '',
  },
  frontImage: {
    type: String,
    default: '',
  },
  
  primaryColor: {
    type: String,
    default: '#000000',
  },
  secondaryColor: {
    type: String,
    default: '#FFFFFF',
  },
  isFlipped: {
    type: Boolean,
    default: false,
  },
  isFalling: {
    type: Boolean,
    default: false,
  },
  isAnimatingOut: {
    type: Boolean,
    default: false,
  },
  isAnimatingIn: {
    type: Boolean,
    default: false,
  },
  reelDelayMs: {
    type: Number,
    default: 0,
  },
  isSelected: {
    type: Boolean,
    default: false,
  },
  selectable: {
    type: Boolean,
    default: false,
  },
  isInJobsDone: {
    type: Boolean,
    default: false,
  },

});

const emit = defineEmits([
  'dragstart',
  'dragover',
  'drop',
  'dragenter',
  'dragleave',
  'show-explanation',
  'select',
]);

const dragOver = ref(false);

const reelStyle = computed(() => ({
  '--reel-delay': `${props.reelDelayMs || 0}ms`,
}));

// Computed properties for dynamic styling
const backStyle = computed(() => {
  const primaryColor = props.primaryColor || '#000000';
  const darkerColor = darkenColor(primaryColor, 15);
  return {
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${darkerColor} 100%)`
  };
});

const titleStyle = computed(() => {
  return {
    backgroundColor: props.secondaryColor || '#FFFFFF',
    color: props.primaryColor || '#000000'
  };
});

// Helper function to darken a color by percentage
const darkenColor = (color, percent) => {
  // Simple color darkening - convert hex to RGB, darken, convert back
  const hex = color.replace('#', '');
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  const factor = (100 - percent) / 100;
  const newR = Math.round(r * factor);
  const newG = Math.round(g * factor);
  const newB = Math.round(b * factor);
  
  return `#${newR.toString(16).padStart(2, '0')}${newG.toString(16).padStart(2, '0')}${newB.toString(16).padStart(2, '0')}`;
};





// Instead of toggling local explanation text, we simply emit an event.
const emitShowExplanation = () => {
  // Emit the explanation HTML up to the parent
  emit('show-explanation', {title: props.title, explanation:props.explanation});
};

const handleClick = () => {
  if (props.selectable) {
    emit('select');
  }
};

const handleDragStart = (event) => {
  // Forward the event to the parent (or higher-level handler)
  emit('dragstart', event);
};

const handleDragOver = (event) => {
  event.preventDefault();
  emit('dragover', event);
};

const handleDragEnter = () => {
  console.log('Drag enter on card:', props.title);
  dragOver.value = true;
  emit('dragenter');
};

const handleDragLeave = (event) => {
  // Check if we're actually leaving the card (not just entering a child element)
  const rect = event.currentTarget.getBoundingClientRect();
  const x = event.clientX;
  const y = event.clientY;
  
  if (x <= rect.left || x >= rect.right || y <= rect.top || y >= rect.bottom) {
    console.log('Drag leave on card:', props.title);
    dragOver.value = false;
    emit('dragleave');
  }
};

const handleDrop = (event) => {
  event.preventDefault();
  dragOver.value = false;
  emit('drop', event);
};
</script>

<style src="../styles/_card.scss" lang="scss" scoped></style>
